import React, { useEffect, useState } from "react";
import Navbar from "../components/common/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { FileText, Trash2, Plus, Check, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyBlog = { title: "", excerpt: "", content: "", image: "", category: "Insights", author: "Yantraksh Team" };
const emptyCase = { title: "", client: "", industry: "", summary: "", challenge: "", solution: "", results: "", image: "" };
const emptyDoc = { user_id: "", type: "purchase_order", title: "", description: "", amount: "" };

const DOC_TYPE_LABEL = { purchase_order: "Purchase Order", contract: "Contract", bill: "Bill", document: "Document" };

const Admin = () => {
  const { user, logout } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [applications, setApplications] = useState([]);
  const [posts, setPosts] = useState([]);
  const [cases, setCases] = useState([]);
  const [users, setUsers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [blogForm, setBlogForm] = useState(emptyBlog);
  const [caseForm, setCaseForm] = useState(emptyCase);
  const [docForm, setDocForm] = useState(emptyDoc);
  const [docFile, setDocFile] = useState(null);

  const fetchData = async () => {
    try {
      const [inqRes, appRes, blogRes, caseRes, userRes, docRes] = await Promise.all([
        axios.get(`${API}/contact/`),
        axios.get(`${API}/careers/`),
        axios.get(`${API}/blog/`),
        axios.get(`${API}/case-studies/`),
        axios.get(`${API}/auth/users`),
        axios.get(`${API}/documents/`),
      ]);
      setInquiries(inqRes.data);
      setApplications(appRes.data);
      setPosts(blogRes.data);
      setCases(caseRes.data);
      setUsers(userRes.data);
      setDocuments(docRes.data);
    } catch (error) {
      toast.error("Failed to fetch some data");
    }
  };

  useEffect(() => { fetchData(); }, []);

  const approvedUsers = users.filter((u) => u.role === "user" && u.status === "approved");

  // ---- Users ----
  const setUserStatus = async (id, action) => {
    try {
      await axios.post(`${API}/auth/users/${id}/${action}`);
      toast.success(`User ${action}d`);
      fetchData();
    } catch { toast.error("Action failed"); }
  };
  const deleteUser = async (id) => {
    try { await axios.delete(`${API}/auth/users/${id}`); toast.success("User deleted"); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  // ---- Documents ----
  const createDoc = async (e) => {
    e.preventDefault();
    if (!docForm.user_id) return toast.error("Select a user");
    const fd = new FormData();
    Object.entries(docForm).forEach(([k, v]) => fd.append(k, v));
    if (docFile) fd.append("file", docFile);
    try {
      await axios.post(`${API}/documents/`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document assigned");
      setDocForm(emptyDoc); setDocFile(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to assign"); }
  };
  const deleteDoc = async (id) => {
    try { await axios.delete(`${API}/documents/${id}`); toast.success("Deleted"); fetchData(); }
    catch { toast.error("Delete failed"); }
  };

  // ---- Blog / Cases ----
  const createBlog = async (e) => { e.preventDefault();
    try { await axios.post(`${API}/blog/`, blogForm); toast.success("Article published"); setBlogForm(emptyBlog); fetchData(); }
    catch { toast.error("Failed"); } };
  const deleteBlog = async (id) => { try { await axios.delete(`${API}/blog/${id}`); toast.success("Deleted"); fetchData(); } catch { toast.error("Failed"); } };
  const createCase = async (e) => { e.preventDefault();
    try { await axios.post(`${API}/case-studies/`, caseForm); toast.success("Case study published"); setCaseForm(emptyCase); fetchData(); }
    catch { toast.error("Failed"); } };
  const deleteCase = async (id) => { try { await axios.delete(`${API}/case-studies/${id}`); toast.success("Deleted"); fetchData(); } catch { toast.error("Failed"); } };

  const statusBadge = (s) => {
    const map = { approved: "bg-green-100 text-green-700", pending: "bg-yellow-100 text-yellow-700", rejected: "bg-red-100 text-red-700" };
    return <Badge className={map[s] || ""}>{s}</Badge>;
  };

  const pendingCount = users.filter((u) => u.status === "pending").length;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      <div className="bg-white shadow-sm border-b py-4 mt-[110px]">
        <div className="container mx-auto px-4 flex justify-between items-center">
          <h1 className="text-xl font-bold font-display uppercase tracking-wider">Admin Dashboard</h1>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500 hidden sm:inline">{user?.email}</span>
            <Button variant="outline" size="sm" onClick={logout}>Logout</Button>
          </div>
        </div>
      </div>

      <main className="flex-grow container mx-auto px-4 py-8">
        <Tabs defaultValue="users" className="w-full">
          <TabsList className="flex flex-wrap h-auto justify-start gap-2 mb-8 bg-transparent p-0">
            <TabsTrigger value="users" data-testid="tab-users" className="data-[state=active]:bg-primary data-[state=active]:text-white border">
              Users {pendingCount > 0 && <span className="ml-1 bg-gold text-slate-900 rounded-full px-1.5 text-xs">{pendingCount}</span>}
            </TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents" className="data-[state=active]:bg-primary data-[state=active]:text-white border">Documents ({documents.length})</TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="tab-inquiries" className="data-[state=active]:bg-primary data-[state=active]:text-white border">Inquiries ({inquiries.length})</TabsTrigger>
            <TabsTrigger value="applications" data-testid="tab-applications" className="data-[state=active]:bg-primary data-[state=active]:text-white border">Applications ({applications.length})</TabsTrigger>
            <TabsTrigger value="blog" data-testid="tab-blog" className="data-[state=active]:bg-primary data-[state=active]:text-white border">Blog ({posts.length})</TabsTrigger>
            <TabsTrigger value="cases" data-testid="tab-cases" className="data-[state=active]:bg-primary data-[state=active]:text-white border">Case Studies ({cases.length})</TabsTrigger>
          </TabsList>

          {/* Users */}
          <TabsContent value="users">
            <div className="bg-white border rounded-sm shadow-sm overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Company</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">No users</TableCell></TableRow>
                  ) : users.map((u) => (
                    <TableRow key={u.id} data-testid="admin-user-row">
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell className="text-sm">{u.email}</TableCell>
                      <TableCell className="text-sm">{u.company || "—"}</TableCell>
                      <TableCell>{u.role}</TableCell>
                      <TableCell>{statusBadge(u.status)}</TableCell>
                      <TableCell>
                        {u.role !== "admin" && (
                          <div className="flex gap-2">
                            {u.status !== "approved" && (
                              <Button size="sm" variant="outline" className="h-8 text-green-600 border-green-200" onClick={() => setUserStatus(u.id, "approve")} data-testid="user-approve">
                                <Check size={14} className="mr-1" /> Approve
                              </Button>
                            )}
                            {u.status !== "rejected" && (
                              <Button size="sm" variant="outline" className="h-8 text-yellow-700 border-yellow-200" onClick={() => setUserStatus(u.id, "reject")} data-testid="user-reject">
                                <X size={14} className="mr-1" /> Reject
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" className="h-8" onClick={() => deleteUser(u.id)} data-testid="user-delete"><Trash2 size={14} className="text-red-500" /></Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Documents */}
          <TabsContent value="documents">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <form onSubmit={createDoc} className="lg:col-span-1 bg-white border p-6 shadow-sm space-y-4 h-fit" data-testid="doc-form">
                <h3 className="font-display font-bold uppercase tracking-wider text-sm flex items-center gap-2"><Plus size={16} /> Assign Document</h3>
                <div className="space-y-1">
                  <Label>User</Label>
                  <Select value={docForm.user_id} onValueChange={(v) => setDocForm({ ...docForm, user_id: v })}>
                    <SelectTrigger data-testid="doc-user" className="rounded-none"><SelectValue placeholder="Select user" /></SelectTrigger>
                    <SelectContent>
                      {approvedUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Type</Label>
                  <Select value={docForm.type} onValueChange={(v) => setDocForm({ ...docForm, type: v })}>
                    <SelectTrigger data-testid="doc-type" className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(DOC_TYPE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label>Title</Label><Input data-testid="doc-title" value={docForm.title} onChange={(e) => setDocForm({ ...docForm, title: e.target.value })} required className="rounded-none" /></div>
                <div className="space-y-1"><Label>Description</Label><Textarea value={docForm.description} onChange={(e) => setDocForm({ ...docForm, description: e.target.value })} className="rounded-none" /></div>
                <div className="space-y-1"><Label>Amount (optional)</Label><Input value={docForm.amount} onChange={(e) => setDocForm({ ...docForm, amount: e.target.value })} className="rounded-none" placeholder="₹..." /></div>
                <div className="space-y-1"><Label>File (optional)</Label><Input type="file" onChange={(e) => setDocFile(e.target.files[0])} className="rounded-none" /></div>
                <Button type="submit" data-testid="doc-submit" className="w-full rounded-none">Assign Document</Button>
              </form>
              <div className="lg:col-span-2 space-y-3">
                {documents.length === 0 ? <p className="text-gray-500 text-sm">No documents assigned yet.</p> : documents.map((d) => (
                  <div key={d.id} className="bg-white border p-4 shadow-sm flex items-center gap-4" data-testid="admin-doc-row">
                    <Badge className="bg-primary text-white shrink-0">{DOC_TYPE_LABEL[d.type]}</Badge>
                    <div className="flex-grow min-w-0">
                      <p className="font-medium truncate">{d.title} {d.amount && <span className="text-gold">· {d.amount}</span>}</p>
                      <p className="text-xs text-gray-500 truncate">For: {d.user_email}{d.file_path ? " · file attached" : ""}</p>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => deleteDoc(d.id)} data-testid="doc-delete"><Trash2 size={18} className="text-red-500" /></Button>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Inquiries */}
          <TabsContent value="inquiries">
            <div className="bg-white border rounded-sm shadow-sm overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Contact</TableHead><TableHead>Company</TableHead><TableHead>Service</TableHead><TableHead>Details</TableHead><TableHead>Source</TableHead></TableRow></TableHeader>
                <TableBody>
                  {inquiries.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">No inquiries</TableCell></TableRow> :
                    inquiries.map((inq) => (
                      <TableRow key={inq.id} data-testid="admin-inquiry-row">
                        <TableCell className="font-mono text-xs">{new Date(inq.created_at).toLocaleDateString()}</TableCell>
                        <TableCell><div className="font-medium">{inq.name}</div><div className="text-xs text-gray-500">{inq.email}</div>{inq.phone && <div className="text-xs text-gray-500">{inq.phone}</div>}</TableCell>
                        <TableCell className="text-sm">{inq.company || "—"}</TableCell>
                        <TableCell className="text-sm">{inq.service || inq.subject || "—"}</TableCell>
                        <TableCell className="max-w-xs truncate text-sm" title={inq.message}>{inq.message}</TableCell>
                        <TableCell><Badge className={inq.source === "chatbot" ? "bg-gold text-slate-900" : ""} variant={inq.source === "chatbot" ? "default" : "secondary"}>{inq.source || "website"}</Badge></TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Applications */}
          <TabsContent value="applications">
            <div className="bg-white border rounded-sm shadow-sm overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Candidate</TableHead><TableHead>Position</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {applications.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center py-8 text-gray-500">No applications</TableCell></TableRow> :
                    applications.map((app) => (
                      <TableRow key={app.id}>
                        <TableCell className="font-mono text-xs">{new Date(app.created_at).toLocaleDateString()}</TableCell>
                        <TableCell><div className="font-medium">{app.name}</div><div className="text-xs text-gray-500">{app.email}</div></TableCell>
                        <TableCell>{app.position}</TableCell>
                        <TableCell><a href={`${BACKEND_URL}/api/${app.resume_path}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-primary hover:underline"><FileText size={16} /> View CV</a></TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Blog */}
          <TabsContent value="blog">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <form onSubmit={createBlog} className="lg:col-span-1 bg-white border p-6 shadow-sm space-y-4 h-fit" data-testid="blog-form">
                <h3 className="font-display font-bold uppercase tracking-wider text-sm flex items-center gap-2"><Plus size={16} /> New Article</h3>
                <div className="space-y-1"><Label>Title</Label><Input data-testid="blog-title" value={blogForm.title} onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })} required className="rounded-none" /></div>
                <div className="space-y-1"><Label>Category</Label><Input value={blogForm.category} onChange={(e) => setBlogForm({ ...blogForm, category: e.target.value })} className="rounded-none" /></div>
                <div className="space-y-1"><Label>Image URL</Label><Input value={blogForm.image} onChange={(e) => setBlogForm({ ...blogForm, image: e.target.value })} className="rounded-none" placeholder="https://..." /></div>
                <div className="space-y-1"><Label>Excerpt</Label><Textarea value={blogForm.excerpt} onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })} className="rounded-none" /></div>
                <div className="space-y-1"><Label>Content</Label><Textarea value={blogForm.content} onChange={(e) => setBlogForm({ ...blogForm, content: e.target.value })} required className="rounded-none min-h-[140px]" /></div>
                <Button type="submit" data-testid="blog-submit" className="w-full rounded-none">Publish Article</Button>
              </form>
              <div className="lg:col-span-2 space-y-4">
                {posts.map((p) => (
                  <div key={p.id} className="bg-white border p-4 shadow-sm flex items-center gap-4" data-testid="admin-blog-row">
                    <img src={p.image} alt={p.title} className="h-16 w-24 object-cover" />
                    <div className="flex-grow"><p className="text-xs text-gold uppercase tracking-wider">{p.category}</p><p className="font-medium">{p.title}</p></div>
                    <Button variant="ghost" size="icon" onClick={() => deleteBlog(p.id)} data-testid="blog-delete"><Trash2 size={18} className="text-red-500" /></Button>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Cases */}
          <TabsContent value="cases">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <form onSubmit={createCase} className="lg:col-span-1 bg-white border p-6 shadow-sm space-y-4 h-fit" data-testid="case-form">
                <h3 className="font-display font-bold uppercase tracking-wider text-sm flex items-center gap-2"><Plus size={16} /> New Case Study</h3>
                <div className="space-y-1"><Label>Title</Label><Input data-testid="case-title" value={caseForm.title} onChange={(e) => setCaseForm({ ...caseForm, title: e.target.value })} required className="rounded-none" /></div>
                <div className="space-y-1"><Label>Client</Label><Input value={caseForm.client} onChange={(e) => setCaseForm({ ...caseForm, client: e.target.value })} className="rounded-none" /></div>
                <div className="space-y-1"><Label>Industry</Label><Input value={caseForm.industry} onChange={(e) => setCaseForm({ ...caseForm, industry: e.target.value })} className="rounded-none" /></div>
                <div className="space-y-1"><Label>Image URL</Label><Input value={caseForm.image} onChange={(e) => setCaseForm({ ...caseForm, image: e.target.value })} className="rounded-none" placeholder="https://..." /></div>
                <div className="space-y-1"><Label>Summary</Label><Textarea value={caseForm.summary} onChange={(e) => setCaseForm({ ...caseForm, summary: e.target.value })} className="rounded-none" /></div>
                <div className="space-y-1"><Label>Challenge</Label><Textarea value={caseForm.challenge} onChange={(e) => setCaseForm({ ...caseForm, challenge: e.target.value })} className="rounded-none" /></div>
                <div className="space-y-1"><Label>Solution</Label><Textarea value={caseForm.solution} onChange={(e) => setCaseForm({ ...caseForm, solution: e.target.value })} className="rounded-none" /></div>
                <div className="space-y-1"><Label>Results</Label><Textarea value={caseForm.results} onChange={(e) => setCaseForm({ ...caseForm, results: e.target.value })} className="rounded-none" /></div>
                <Button type="submit" data-testid="case-submit" className="w-full rounded-none">Publish Case Study</Button>
              </form>
              <div className="lg:col-span-2 space-y-4">
                {cases.map((c) => (
                  <div key={c.id} className="bg-white border p-4 shadow-sm flex items-center gap-4" data-testid="admin-case-row">
                    <img src={c.image} alt={c.title} className="h-16 w-24 object-cover" />
                    <div className="flex-grow"><p className="text-xs text-gold uppercase tracking-wider">{c.industry}</p><p className="font-medium">{c.title}</p></div>
                    <Button variant="ghost" size="icon" onClick={() => deleteCase(c.id)} data-testid="case-delete"><Trash2 size={18} className="text-red-500" /></Button>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Admin;
